import React, { forwardRef, useImperativeHandle } from 'react';
import { Box } from '@mui/material';
import { usePerspectiveCropperLogic } from '../../../hooks/logic/usePerspectiveCropperLogic';
import type { PerspectiveCropperHandle } from '../../../types/perspectiveCropper';
import PerspectiveCropperFrame from './PerspectiveCropperFrame';

interface PerspectiveCropperCanvasProps {
  imageUrl: string;
  file: File;
  onChange?: (state: { loading?: boolean }) => void;
  onDragStop?: (state: { loading?: boolean }) => void;
}

const PerspectiveCropperCanvas = forwardRef<PerspectiveCropperHandle, PerspectiveCropperCanvasProps>(
  ({ imageUrl, file, onChange, onDragStop }, ref) => {
    const {
      containerRef,
      previewCanvasRef,
      overlayRef,
      loading,
      frameSize,
      handlePointerDown,
      handlePointerMove,
      handlePointerUp,
      handlePointerLeave,
      backToCrop,
      done,
      setHorizontalTilt,
      setVerticalTilt,
      setRotation,
    } = usePerspectiveCropperLogic({ imageUrl, file, onChange, onDragStop });

    useImperativeHandle(
      ref,
      () => ({
        backToCrop,
        done,
        setHorizontalTilt,
        setVerticalTilt,
        setRotation,
      }),
      [backToCrop, done, setHorizontalTilt, setRotation, setVerticalTilt],
    );

    return (
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          minHeight: 260,
          bgcolor: 'grey.900',
          borderRadius: 1,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <canvas
          ref={previewCanvasRef}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            display: 'block',
          }}
        />
        <PerspectiveCropperFrame
          frameSize={frameSize}
          overlayRef={overlayRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerLeave}
        />
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0,0,0,0.3)',
              zIndex: 20,
            }}
          >
            <span style={{ color: 'white', fontSize: 12 }}>Loading...</span>
          </Box>
        )}
      </Box>
    );
  },
);

PerspectiveCropperCanvas.displayName = 'PerspectiveCropperCanvas';

export default PerspectiveCropperCanvas;
